"use client"

import { useState, useEffect } from "react"
import { TrendingUp, TrendingDown } from "lucide-react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart"
import CotizacionesService from '../../services/cotizacionesService';
import { logError } from '../../lib/utils';

const chartConfig = {
  monto_uf: {
    label: "Monto UF",
    color: "var(--primary)",
  },
}

export default function CotizacionesChart() {
  const [chartData, setChartData] = useState([])
  const [loading, setLoading] = useState(true)
  const [trend, setTrend] = useState({ percentage: 0, isPositive: true })

  useEffect(() => {
    const loadCotizacionesData = async () => {
      try {
        setLoading(true)
        // Usar el nuevo método de estadísticas
        const estadisticas = await CotizacionesService.getEstadisticas()
        
        // Procesar los datos para el gráfico - tomar los últimos 30 días
        const processedData = estadisticas
          .slice(-30)
          .map(item => ({
            fecha: item.fecha,
            monto_uf: item.total_uf,
            count: item.count
          }))
        
        // Calcular tendencia
        if (processedData.length >= 2) {
          const lastValue = processedData[processedData.length - 1].monto_uf
          const previousValue = processedData[processedData.length - 2].monto_uf
          const percentage = previousValue > 0 ? ((lastValue - previousValue) / previousValue) * 100 : 0
          
          setTrend({
            percentage: Math.abs(percentage),
            isPositive: percentage >= 0
          })
        }
        
        setChartData(processedData)
      } catch (error) {
        logError("Error loading cotizaciones data:", error, { ignoreAbort: true })
      } finally {
        setLoading(false)
      }
    }

    loadCotizacionesData()
  }, [])

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cotizaciones en UF</CardTitle>
          <CardDescription>Cargando datos...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center items-center h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    )
  }

  const totalUF = chartData.reduce((sum, item) => sum + item.monto_uf, 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cotizaciones en UF</CardTitle>
        <CardDescription>
          Montos cotizados en Unidades de Fomento - Últimos 30 días
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig}>
          <AreaChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="fecha"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => {
                const date = new Date(value)
                return date.toLocaleDateString("es-CL", {
                  month: "short",
                  day: "numeric",
                })
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => `${value.toFixed(0)} UF`}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => {
                    return new Date(value).toLocaleDateString("es-CL", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })
                  }}
                  formatter={(value) => [`${parseFloat(value).toFixed(2)} UF`, "Monto UF"]}
                />
              }
            />
            <defs>
              <linearGradient id="fillMontoUF" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-monto_uf)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-monto_uf)"
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <Area
              dataKey="monto_uf"
              type="natural"
              fill="url(#fillMontoUF)"
              fillOpacity={0.4}
              stroke="var(--color-monto_uf)"
              strokeWidth={2}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="flex gap-2 font-medium leading-none">
          {trend.isPositive ? (
            <>
              Incremento del {trend.percentage.toFixed(1)}% respecto al día anterior
              <TrendingUp className="h-4 w-4" />
            </>
          ) : (
            <>
              Disminución del {trend.percentage.toFixed(1)}% respecto al día anterior
              <TrendingDown className="h-4 w-4" />
            </>
          )}
        </div>
        <div className="leading-none text-muted-foreground">
          Total acumulado: {totalUF.toFixed(2)} UF
        </div>
      </CardFooter>
    </Card>
  )
}
